import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, Copy, Send, Loader } from 'lucide-react';
import { useUser } from '../context/UserContext';
import { QRCodeSVG as QRCode } from 'qrcode.react';
import { toast } from 'react-toastify';
import DashboardLayout from '../components/DashboardLayout';

const API = 'https://tradebot-k4nt.onrender.com/api';
const BASE_URL = 'https://tradebot-k4nt.onrender.com';

export default function WalletDetail() {
  const navigate = useNavigate();
  const { cryptoType } = useParams();
  const { user } = useUser();

  const [loading, setLoading] = useState(true);
  const [balance, setBalance] = useState('0');
  const [address, setAddress] = useState('');

  useEffect(() => {
    if (!user?.telegramName) {
      navigate('/login');
      return;
    }
    fetchWalletData();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, cryptoType, navigate]);

  const fetchWalletData = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API}/payment/getCryptoWallet-id-balance/${user.telegramName}/${cryptoType}`);
      const data = await res.json();
      
      const walletData = data.respond[cryptoType];
      setAddress(walletData.address);
      setBalance(walletData.balance);
    } catch (err) {
      toast.error('Failed to load wallet data');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleCopyAddress = async () => {
    try {
      await navigator.clipboard.writeText(address);
      toast.success('Address copied!');
    } catch {
      toast.error('Failed to copy');
    }
  };

  const qrValue = `${BASE_URL}/wallet/${cryptoType}/send?address=${address}`;

  if (loading) {
    return (
      <DashboardLayout>
        <div className="min-h-screen flex items-center justify-center">
          <Loader className="animate-spin h-12 w-12 text-blue-600" />
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="max-w-2xl mx-auto px-4 py-8">
        <button onClick={() => navigate('/wallet')} className="flex items-center gap-2 text-slate-700 hover:text-slate-900 mb-6 transition">
          <ArrowLeft size={20} /> Back
        </button>

        <div className="bg-white rounded-2xl shadow-sm border border-slate-200 p-8">
          <h3 className="text-2xl font-bold text-center mb-2">{cryptoType} Wallet</h3>
          <p className="text-lg text-center text-slate-600 mb-6">Balance: <span className="font-bold text-slate-900">{balance} {cryptoType}</span></p>

          <div className="flex justify-center mb-6 p-6 bg-slate-50 rounded-2xl">
            <QRCode value={qrValue} size={200} level="H" includeMargin={true} />
          </div>

          <div className="bg-slate-50 rounded-xl p-4 mb-6">
            <p className="text-sm text-slate-600 mb-2">Wallet Address</p>
            <div className="flex gap-2">
              <div className="flex-1 px-3 py-2 bg-white border border-slate-200 rounded-lg text-sm font-mono text-slate-700 overflow-hidden text-ellipsis whitespace-nowrap">
                {address}
              </div>
              <button onClick={handleCopyAddress} className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition shrink-0 shadow-sm">
                <Copy size={16} />
              </button>
            </div>
          </div>

          <button
            onClick={() => navigate(`/wallet/${cryptoType}/send`)}
            className="w-full py-3 bg-blue-600 text-white font-semibold rounded-xl hover:bg-blue-700 transition flex items-center justify-center gap-2 shadow-sm"
          >
            <Send size={18} /> Send {cryptoType}
          </button>
        </div>
      </div>
    </DashboardLayout>
  );
}
